import { config } from '../../config';

export interface OllamaResponse {
  response: string;
  done: boolean;
}

export interface OllamaRequest {
  model: string;
  prompt: string;
  stream: boolean;
  options?: {
    temperature: number;
    top_p: number;
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export type AIProvider = 'ollama' | 'freellmapi';

export interface AIProviderConfig {
  name: AIProvider;
  baseUrl: string;
  apiKey?: string;
  defaultModel: string;
  headers?: Record<string, string>;
  _proxyTarget?: string; // When set, routes through worker proxy for CORS bypass
}

// ─── AI Provider Configuration ───────────────────────────────────────────────

function getProviderConfigs(): AIProviderConfig[] {
  const configs: AIProviderConfig[] = [];

  // Tier 1: FreeLLMAPI (via worker proxy — bypasses CORS restrictions)
  // The PWA cannot call localhost:3001 directly due to CORS, so we route
  // through the Cloudflare Worker at API_BASE_URL which already has CORS headers.
  // The worker proxies server-to-server to FreeLLMAPI at localhost:3001.
  const workerProxyUrl = config.API_BASE_URL
    ? `${config.API_BASE_URL}/ai/chat`
    : config.FREELLMAPI_BASE_URL + '/v1/chat/completions';

  configs.push({
    name: 'freellmapi',
    baseUrl: workerProxyUrl,
    apiKey: config.FREELLMAPI_KEY || '',
    defaultModel: 'auto',
    headers: { 'Content-Type': 'application/json' },
    // When using the worker proxy, the body must include { provider: 'freellmapi' }
    _proxyTarget: 'freellmapi',
  });

  // Tier 2: Local Ollama (localhost:11434 — gemma3:1b, qwen3-0.6b, bge-m3)
  configs.push({
    name: 'ollama',
    baseUrl: config.OLLAMA_BASE_URL || 'http://localhost:11434',
    defaultModel: 'gemma3:1b-it-qat',
  });

  // Tier 3: HuggingFace Ollama Cloud (gemma4:31b via HuggingFace)
  // Accessible via FreeLLMAPI's HuggingFace provider route
  // No separate config needed — FreeLLMAPI handles the HF routing

  return configs;
}

class OllamaService {
  private providers: AIProviderConfig[];
  private activeProvider: AIProvider;

  constructor() {
    this.providers = getProviderConfigs();
    this.activeProvider = (config.AI_PROVIDER as AIProvider) || 'ollama';
  }

  getActiveProvider(): AIProviderConfig {
    return this.providers.find(p => p.name === this.activeProvider) || this.providers[0];
  }

  setActiveProvider(name: AIProvider) {
    const provider = this.providers.find(p => p.name === name);
    if (provider) {
      this.activeProvider = name;
    }
  }

  getProviders(): AIProviderConfig[] {
    return [...this.providers];
  }

  async generateText(prompt: string, model?: string): Promise<string> {
    // Try providers in priority order: FreeLLMAPI → Ollama (local) → error
    const errors: string[] = [];

    for (const provider of this.providers) {
      try {
        const modelName = model || provider.defaultModel;

        if (provider.name === 'ollama') {
          return await this._ollamaGenerate(prompt, modelName, provider.baseUrl);
        }

        return await this._openaiGenerate(prompt, modelName, provider);
      } catch (err: any) {
        console.warn(`[AI] Provider ${provider.name} failed:`, err.message);
        errors.push(`${provider.name}: ${err.message}`);
        // Continue to next provider
      }
    }

    throw new Error(`All AI providers failed: ${errors.join('; ')}`);
  }

  async generateChat(messages: ChatMessage[], model?: string): Promise<string> {
    const provider = this.getActiveProvider();
    const modelName = model || provider.defaultModel;

    if (provider.name === 'ollama') {
      return this._ollamaChat(messages, modelName, provider.baseUrl);
    }

    return this._openaiChat(messages, modelName, provider);
  }

  private async _ollamaGenerate(prompt: string, model: string, baseUrl: string): Promise<string> {
    const response = await fetch(`${baseUrl}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.HF_TOKEN}`
      },
      body: JSON.stringify({
        model,
        prompt,
        stream: false,
        options: { temperature: 0.7, top_p: 0.9 },
      }),
    });


    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.statusText}`);
    }

    const result: OllamaResponse = await response.json();
    return result.response;
  }

  private async _ollamaChat(messages: ChatMessage[], model: string, baseUrl: string): Promise<string> {
    const response = await fetch(`${baseUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.HF_TOKEN}`
      },
      body: JSON.stringify({ model, messages, stream: false }),
    });


    if (!response.ok) {
      throw new Error(`Ollama Chat error: ${response.statusText}`);
    }

    const result = await response.json();
    return result.message.content;
  }

  private async _openaiGenerate(prompt: string, model: string, provider: AIProviderConfig): Promise<string> {
    const messages: ChatMessage[] = [{ role: 'user', content: prompt }];
    return this._openaiChat(messages, model, provider);
  }

  private async _openaiChat(messages: ChatMessage[], model: string, provider: AIProviderConfig): Promise<string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...provider.headers,
    };

    // Build the request body
    const body: any = { model, messages, stream: false };

    // When using the worker proxy (to bypass CORS), include the proxy target
    // and DON'T send the API key (the worker adds its own FreeLLMAPI key).
    // The worker expects { provider, model, messages } — not v1/chat/completions.
    if (provider._proxyTarget) {
      body.provider = provider._proxyTarget;
      // Worker proxy adds its own Authorization header — skip our key
      delete headers['Authorization'];
      // BaseUrl already points to /ai/chat, no extra path needed
    } else if (provider.apiKey) {
      headers['Authorization'] = `Bearer ${provider.apiKey}`;
    }

    const url = provider._proxyTarget
      ? provider.baseUrl  // already full URL to /ai/chat
      : `${provider.baseUrl}/v1/chat/completions`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`FreeLLMAPI error: ${response.statusText}`);
    }

    const result = await response.json();
    return result.choices?.[0]?.message?.content || '';
  }

  async healthCheck(): Promise<{ provider: AIProvider; healthy: boolean; latencyMs: number }[]> {
    const results: { provider: AIProvider; healthy: boolean; latencyMs: number }[] = [];

    for (const provider of this.providers) {
      const start = Date.now();
      let healthy = false;

      try {
        if (provider.name === 'ollama') {
          const res = await fetch(`${provider.baseUrl}/api/tags`, { signal: AbortSignal.timeout(5000) });
          healthy = res.ok;
        } else {
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          if (provider.apiKey) {headers['Authorization'] = `Bearer ${provider.apiKey}`;}
          const res = await fetch(`${provider.baseUrl}/v1/models`, { headers, signal: AbortSignal.timeout(5000) });
          healthy = res.ok;
        }
      } catch {
        healthy = false;
      }

      results.push({ provider: provider.name, healthy, latencyMs: Date.now() - start });
    }

    return results;
  }

  async autoSelectProvider(): Promise<AIProvider> {
    // In browser, health checks will fail on CORS for external APIs.
    // Instead, just use the first provider in priority order and let
    // generateText's fallback chain handle failures at request time.
    // The generateText method already tries every provider in order.
    if (this.providers.length > 0) {
      // Always prefer FreeLLMAPI if available (first in providers array)
      this.activeProvider = this.providers[0].name;
      return this.activeProvider;
    }
    return this.activeProvider;
  }
}

export const ollamaService = new OllamaService();
